import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Loader2, BarChart3, Users, MessageSquare, Calendar,
  DollarSign, AlertTriangle, CheckCircle, XCircle,
  TrendingUp, Globe, Shield, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Stat Card Component
 */
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600'
  };
  
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
            {trend && (
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="h-3 w-3 text-green-500" />
                <span className="text-xs text-green-600">{trend}</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colors[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Platform Admin Dashboard
 */
const PlatformAdminDashboard = () => {
  const [adminKey, setAdminKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [bookingAnalytics, setBookingAnalytics] = useState(null);
  const [smsStats, setSmsStats] = useState(null);
  const [providers, setProviders] = useState([]);
  const [flaggedUsers, setFlaggedUsers] = useState(null);

  const fetchData = async (key) => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [dashRes, analyticsRes, smsRes, providersRes, flaggedRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/dashboard?admin_key=${key}`),
        fetch(`${API_URL}/api/admin/bookings/analytics?admin_key=${key}&days=30`),
        fetch(`${API_URL}/api/admin/sms/by-country?admin_key=${key}&days=30`),
        fetch(`${API_URL}/api/admin/providers/list?admin_key=${key}&limit=20`),
        fetch(`${API_URL}/api/admin/abuse/flagged-users?admin_key=${key}`)
      ]);

      if (!dashRes.ok) {
        throw new Error('Invalid admin key');
      }

      setDashboard(await dashRes.json());
      setBookingAnalytics(await analyticsRes.json());
      setSmsStats(await smsRes.json());
      const provData = await providersRes.json();
      setProviders(provData.providers || []);
      setFlaggedUsers(await flaggedRes.json());
      setAuthenticated(true);
      
    } catch (error) {
      toast.error(error.message);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    if (!adminKey) {
      toast.error('Please enter admin key');
      return;
    }
    fetchData(adminKey);
  };

  const handleApproveProvider = async (providerId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/admin/providers/${providerId}/approve?admin_key=${adminKey}`,
        { method: 'POST' }
      );
      if (res.ok) {
        toast.success('Provider approved');
        fetchData(adminKey);
      }
    } catch (e) {
      toast.error('Failed to approve provider');
    }
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              KEIKKA Admin
            </CardTitle>
            <CardDescription>Enter admin key to access dashboard</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              type="password"
              placeholder="Admin API Key"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
            <Button className="w-full" onClick={handleLogin} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Access Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-orange-500" />
            <h1 className="text-xl font-bold text-gray-900">KEIKKA Admin Dashboard</h1>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchData(adminKey)}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Overview */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard
              title="Total Bookings"
              value={dashboard.bookings.total}
              subtitle={`${dashboard.bookings.today} today`}
              icon={Calendar}
              color="blue"
            />
            <StatCard
              title="Active Providers"
              value={dashboard.providers.active}
              subtitle={`${dashboard.providers.pending_approval} pending`}
              icon={Users}
              color="green"
            />
            <StatCard
              title="SMS Sent"
              value={dashboard.sms.total_sent}
              subtitle={`${dashboard.sms.sent_today} today`}
              icon={MessageSquare}
              color="purple"
            />
            <StatCard
              title="PRO Subscribers"
              value={dashboard.users.pro_subscribers}
              subtitle={`of ${dashboard.users.total} users`}
              icon={DollarSign}
              color="orange"
            />
          </div>
        )}

        <Tabs defaultValue="bookings" className="space-y-4">
          <TabsList>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="sms">SMS & Revenue</TabsTrigger>
            <TabsTrigger value="providers">Providers</TabsTrigger>
            <TabsTrigger value="abuse">Abuse Prevention</TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {bookingAnalytics && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Bookings by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {bookingAnalytics.by_category.slice(0, 8).map((cat) => (
                        <div key={cat.category} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{cat.category || 'Unknown'}</span>
                          <Badge variant="secondary">{cat.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Bookings by Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {bookingAnalytics.by_status.map((s) => (
                        <div key={s.status} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {s.status === 'confirmed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                            {s.status === 'pending' && <Loader2 className="h-4 w-4 text-yellow-500" />}
                            {s.status === 'cancelled' && <XCircle className="h-4 w-4 text-red-500" />}
                            <span className="text-sm text-gray-600 capitalize">{s.status || 'Unknown'}</span>
                          </div>
                          <Badge variant="secondary">{s.count}</Badge>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Quick Response Usage</span>
                        <Badge className="bg-orange-100 text-orange-700">
                          {bookingAnalytics.quick_response.percentage}%
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Bookings by Country</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {bookingAnalytics.by_country.slice(0, 10).map((c) => (
                        <div key={c.country} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{c.country || 'Unknown'}</span>
                          </div>
                          <Badge variant="secondary">{c.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* SMS Tab */}
          <TabsContent value="sms">
            {smsStats && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">SMS Revenue Summary</CardTitle>
                    <CardDescription>Last 30 days</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                          {smsStats.totals.total_sms}
                        </div>
                        <div className="text-xs text-gray-500">SMS Sent</div>
                      </div>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">
                          €{smsStats.totals.total_cost_eur}
                        </div>
                        <div className="text-xs text-gray-500">Cost</div>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          €{smsStats.totals.total_margin_eur}
                        </div>
                        <div className="text-xs text-gray-500">Margin</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">SMS by Country</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {smsStats.by_country.map((c) => (
                        <div key={c.country} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">{c.country}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-400">{c.sms_count} SMS</span>
                            <Badge className="bg-green-100 text-green-700">
                              €{c.margin_eur} margin
                            </Badge>
                          </div>
                        </div>
                      ))}
                      {smsStats.by_country.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">No SMS sent yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Providers</CardTitle>
                <CardDescription>Manage provider accounts</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {providers.map((p) => (
                    <div 
                      key={p.provider_id} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-sm text-gray-500">
                          {p.email} • {p.city}, {p.country}
                        </div>
                        <div className="flex gap-1 mt-1">
                          {p.service_categories?.slice(0, 3).map((cat) => (
                            <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          p.status === 'approved' ? 'bg-green-100 text-green-700' :
                          p.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {p.status}
                        </Badge>
                        {p.status === 'pending' && (
                          <Button 
                            size="sm" 
                            onClick={() => handleApproveProvider(p.provider_id)}
                          >
                            Approve
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Abuse Prevention Tab */}
          <TabsContent value="abuse">
            {flaggedUsers && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      High SMS Users (Today)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {flaggedUsers.high_sms_users.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No users flagged
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {flaggedUsers.high_sms_users.map((u) => (
                          <div key={u.user_id} className="flex items-center justify-between p-2 bg-amber-50 rounded">
                            <span className="text-sm font-mono">{u.user_id}</span>
                            <Badge variant="destructive">{u.sms_today} SMS</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <XCircle className="h-5 w-5 text-red-500" />
                      High Decline Providers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {flaggedUsers.high_decline_providers.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No providers flagged
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {flaggedUsers.high_decline_providers.map((p) => (
                          <div key={p.provider_id} className="flex items-center justify-between p-2 bg-red-50 rounded">
                            <span className="text-sm font-mono">{p.provider_id}</span>
                            <Badge variant="destructive">{p.declines_this_week} declines</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PlatformAdminDashboard;
